import * as React from "react";
import * as _ from "lodash";
import styled, { css } from "styled-components";

import Grid from "src/library/components/Hedron";
import { Text } from "src/library/components/Text/Text";
import Dropdown from "src/library/components/Dropdown/Dropdown";
import { ITag } from "src/modules/product-tags/api/product-tags.api";

import ProductTag from "./ProductTag";

interface ProductTagFilterProps {
  handleSearch: (type: "$in" | "$notIn", tags?: number[]) => void;
  tagType: string;
  productTags: ITag[];
  isLoading?: boolean;
}

const ProductTagFilter: React.ReactFCWithChildren<ProductTagFilterProps> = ({
  isLoading, handleSearch, productTags, tagType
}) => {
  const [ isOpen, setIsOpen ] = React.useState(false);
  const [ selectedTags, setSelectedTags ] = React.useState<number[]>([]);

  /**
   * handle the click of a tag
   */
  const onTagClick = React.useCallback(
    (tag: ITag) => {
      const tagIndex = _.findIndex(selectedTags, tagId => tagId === tag.id);

      if (tagIndex !== -1) {
        selectedTags.splice(tagIndex, 1);
      } else {
        selectedTags.push(tag.id);
      }

      const newSelected = [ ...selectedTags ];

      setSelectedTags(newSelected);

      if (tagType === "Allergens & Warnings") {
        handleSearch("$notIn", newSelected);
      } else {
        handleSearch("$in", newSelected);
      }
    },
    [
      tagType,
      selectedTags,
      handleSearch
    ]
  );

  /**
   * Reset tags to default
   */
  const reset = React.useCallback(() => {
    if (tagType === "Allergens & Warnings") {
      handleSearch("$notIn", []);
    } else {
      handleSearch("$in", []);
    }

    setSelectedTags([]);
    setIsOpen(false);
  }, [
    tagType,
    productTags,
    handleSearch
  ]);

  // number of selected items
  const selectCount = React.useMemo(() => {
    return selectedTags.length;
  }, [
    productTags,
    tagType,
    selectedTags
  ]);

  return (
    <Dropdown
      isLoading={isLoading}
      isOpen={isOpen}
      toggleOpen={newIsOpen => setIsOpen(typeof newIsOpen === "boolean" ? newIsOpen : !isOpen)}
      title={`${tagType} (${selectCount})`}
      DropdownInnerComponent={() => (
        <DropdownInner direction="vertical">
          <HeaderItem direction="horizontal">
            <Text weight="bold">
              {`${tagType === "Allergens & Warnings" ? "Remove products with" : "Show products that are"} `}
            </Text>

            {!!selectCount && (
              <div
                className="clear"
                onClick={() => reset()}
              >
                <Text>
                  Clear
                </Text>
              </div>
            )}
          </HeaderItem>

          {_.map(productTags, tag => (
            <TagItem
              key={tag.id}
              direction="horizontal"
              className={`${selectedTags.includes(tag.id) && "active"}`}
              onClick={() => onTagClick(tag)}
            >
              <ProductTag {...tag} />

              <Text className="tag-text">
                {tag.label}
              </Text>
            </TagItem>
          ))}
        </DropdownInner>
      )}
    />
  );
};

const DropdownInner = styled(Grid.Bounds)`
  width: 200px;
`;

const TagItem = styled(Grid.Box)`
  ${({ theme }) => css`
    padding: ${theme.spacing.padding.md};
    padding-left: 50px;
    transition: ${theme.transition.quick};
    position: relative;
    opacity: 0.5;

    .icon-wrapper {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      left: ${theme.spacing.padding.sm};
    }

    &:hover {
      cursor: pointer;
    }

    &.active {
      opacity: 1;
      .tag-text {
        font-weight: ${theme.fonts.weights.bold};
      }
    }
  `}
`;

const HeaderItem = styled(Grid.Box)`
  ${({ theme }) => css`
    display: flex;
    padding: ${theme.spacing.padding.md};
    border-bottom: 1px solid ${theme.colors.lightGrey};
    transition: ${theme.transition.quickest};
    justify-content: space-between;

    .clear {
      display: inline-block;
      &:hover {
        color: ${theme.colors.primaryDark};
        cursor: pointer;
      }
    }
  `}
`;

export default ProductTagFilter;
